import { getApiErrorMessage } from "@/api/utils";
import { useFileRecordExport } from "@/panes/file-record-detail/FileRecordExportContext";
import { FileRecordSelectionActionOverlay } from "@/panes/file-record-detail/FileRecordSelectionActionOverlay";
import { useDomTextSelectionAction } from "@/panes/file-record-detail/useDomTextSelectionAction";
import { usePushFileRecordTextSelectionToChat } from "@/panes/file-record-detail/usePushFileRecordTextSelectionToChat";
import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import {
  fileRecordVersionKey,
  useFileRecordText,
} from "@cohesive/ui-components";
import { useEffect, useRef } from "react";
import { getFileRecordName } from "../contentType";
import { LoadingOverlay } from "../LoadingOverlay";
import { useFileRecordVersionPreview } from "../versioning/FileRecordVersioningContext";

/**
 * Covers text/plain, text/csv, text/html (raw source), application/json,
 * application/xml, text/x-rst — anything where a monospace dump is the
 * honest rendering. Markdown has its own `MarkdownContent` renderer.
 */
export function TextContent({ record }: { record: FileRecord }) {
  const { setTextSource } = useFileRecordExport();
  const { previewFetchUrl, isPreviewing } = useFileRecordVersionPreview();
  const contentRef = useRef<HTMLDivElement>(null);
  const fetchUrl = previewFetchUrl ?? record.url;
  const {
    data: text = null,
    isPending,
    error: loadError,
  } = useFileRecordText({
    url: fetchUrl,
    versionKey: previewFetchUrl ?? fileRecordVersionKey(record),
  });
  const isLoading = !!fetchUrl && isPending;
  const error = loadError ? getApiErrorMessage(loadError) : null;

  const { enabled: textSelectionEnabled, pushSelection } =
    usePushFileRecordTextSelectionToChat({
      fileRecordId: record.id,
      fileRecordName: getFileRecordName(record),
    });
  const canReferenceSelectionInChat = textSelectionEnabled && !isPreviewing;

  const { selectedText, anchorRect, clearSelection } =
    useDomTextSelectionAction({
      enabled: canReferenceSelectionInChat && text != null,
      rootRef: contentRef,
    });

  useEffect(() => {
    setTextSource(text);
    return () => setTextSource(null);
  }, [text, setTextSource]);

  if (!fetchUrl) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        File not available.
      </div>
    );
  }

  return (
    <div ref={contentRef} className="relative h-full w-full overflow-auto">
      <pre
        className={cn(
          "whitespace-pre-wrap break-words font-mono text-sm text-foreground p-4 transition-opacity duration-300",
          !isLoading && text != null ? "opacity-100" : "opacity-0",
        )}
      >
        {text}
      </pre>
      {isLoading && <LoadingOverlay />}
      {error && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
          {error}
        </div>
      )}
      {canReferenceSelectionInChat && (
        <FileRecordSelectionActionOverlay
          selectedText={selectedText}
          anchorRect={anchorRect}
          onActivate={pushSelection}
          onClear={clearSelection}
        />
      )}
    </div>
  );
}
